import { useState } from 'react';
import { Collapse, Group, Text, Paper, Stack, Button, Box, UnstyledButton } from '@mantine/core';
import { ChevronDown, ChevronRight } from 'react-feather';

interface EmailPreviewProps {
  subject: string;
  body: string;
  recipientName: string;
  recipientEmail: string;
  surveyLinkText?: string;
}

/**
 * Collapsible email preview showing rendered email with sample data
 */
export function EmailPreview({
  subject,
  body,
  recipientName,
  recipientEmail,
  surveyLinkText = 'Complete the Survey',
}: EmailPreviewProps) {
  const [expanded, setExpanded] = useState(false);

  // Check if body has form_link placeholder (to show CTA button)
  const hasFormLink = body.includes('{{form_link}}') ||
    body.includes('{{survey_link}}') ||
    body.includes('[Survey Link]');

  // Strip form_link placeholders from body text (they'll be replaced with CTA button)
  const cleanBody = body
    .replace(/\{\{form_link\}\}/g, '')
    .replace(/\{\{survey_link\}\}/g, '')
    .replace(/\[Survey Link\]/g, '')
    .replace(/\n{3,}/g, '\n\n'); // Collapse multiple blank lines

  // Convert markdown-style bold (**text**) to styled text
  const renderBody = (text: string) => {
    const parts = text.split(/\*\*(.+?)\*\*/g);
    return parts.map((part, index) => {
      if (index % 2 === 1) {
        // This is bold text
        return (
          <Text key={index} component="span" fw={600}>
            {part}
          </Text>
        );
      }
      // Handle newlines
      return part.split('\n').map((line, lineIndex) => (
        <span key={`${index}-${lineIndex}`}>
          {lineIndex > 0 && <br />}
          {line}
        </span>
      ));
    });
  };

  return (
    <div>
      <UnstyledButton onClick={() => setExpanded(!expanded)} w="100%">
        <Group gap="xs" mb="xs">
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          <Text size="sm" fw={500}>
            Preview Email
          </Text>
        </Group>
      </UnstyledButton>

      <Collapse in={expanded}>
        <Box bg="gray.1" p="md" style={{ borderRadius: 'var(--mantine-radius-md)' }}>
          <Text size="xs" fw={500} c="dimmed" mb="sm" tt="uppercase">
            Preview (as seen by {recipientName})
          </Text>

          <Paper shadow="xs" p="md" bg="white">
            <Stack gap="sm">
              {/* Email Header */}
              <div>
                <Text size="xs" c="dimmed">
                  From: <Text component="span" fw={500}>Medley</Text> &lt;noreply@medley.com&gt;
                </Text>
                <Text size="sm" fw={600} mt="xs">
                  {subject}
                </Text>
              </div>

              {/* Divider */}
              <Box style={{ borderTop: '1px solid var(--mantine-color-gray-3)' }} my="xs" />

              {/* Email Body */}
              <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                {renderBody(cleanBody)}
              </Text>

              {/* Survey CTA Button - show if template has form_link merge tag */}
              {hasFormLink && (
                <Button
                  component="span"
                  size="sm"
                  color="medleyGreen"
                  style={{ alignSelf: 'flex-start', cursor: 'default' }}
                >
                  {surveyLinkText} →
                </Button>
              )}

              {/* Footer */}
              <Box style={{ borderTop: '1px solid var(--mantine-color-gray-3)' }} mt="md" pt="sm">
                <Text size="xs" c="dimmed">
                  This email was sent to {recipientEmail}
                </Text>
              </Box>
            </Stack>
          </Paper>
        </Box>
      </Collapse>
    </div>
  );
}

export default EmailPreview;
